import api from '@/api/axiosApi'
import {
  AssortmentPlan,
  AssortmentPlansFilters,
  AssortmentPlansResponse,
  ApiPlanSummary,
  ApiPlanStatus,
  ApiHierarchySummary,
  PaginatedPlansResponse,
  PlanStatus,
  ApiPlanDetailResponse,
} from '@/types'
import { formatUserDisplayName } from '@/utils/formatters'

// Mapping functions from API format to frontend display format

function mapApiStatusToDisplay(apiStatus: ApiPlanStatus): PlanStatus {
  switch (apiStatus) {
    case 'DRAFT':
      return 'Draft'
    case 'IN_PROGRESS':
      return 'In Progress'
    case 'SUBMITTED':
      return 'Submitted'
    case 'APPROVED':
      return 'Approved'
    case 'REJECTED':
      return 'Rejected'
    case 'SENT_BACK':
      return 'Sent Back'
    default:
      return 'Draft'
  }
}

function buildHierarchyPath(hierarchy?: ApiHierarchySummary): string {
  if (!hierarchy) return '-'

  const levels = ['DIVISION', 'DEPARTMENT', 'CLASS', 'SUBCLASS'] as const
  const parts: string[] = []

  // Add lineage items in order
  if (hierarchy.lineage) {
    for (const level of levels) {
      const item = hierarchy.lineage.find(l => l.level === level)
      if (item) parts.push(item.name)
    }
  }

  // Add current node if its level comes after lineage
  if (hierarchy.level && hierarchy.name) {
    parts.push(hierarchy.name)
  }

  return parts.length > 0 ? parts.join(' / ') : '-'
}

export function mapApiPlanToFrontend(apiPlan: ApiPlanSummary): AssortmentPlan {
  return {
    id: apiPlan.id,
    name: apiPlan.name,
    hierarchy: buildHierarchyPath(apiPlan.hierarchy),
    hierarchy_id: apiPlan.hierarchy?.id ?? null,
    versions_count: apiPlan.versions_count ?? 0,
    owner: formatUserDisplayName(apiPlan.owner),
    owner_id: apiPlan.owner?.id ?? null,
    status: mapApiStatusToDisplay(apiPlan.status ?? 'DRAFT'),
    is_frozen: apiPlan.is_frozen ?? false,
    active_version_id: apiPlan.active_version_id ?? null,
    active_version_alias: apiPlan.active_version_alias ?? '',
    can_access: apiPlan.can_access ?? true,
  }
}

// Map frontend filter values to API format
export function mapStatusToApi(status: string): string | undefined {
  if (!status || status === 'all') return undefined
  switch (status) {
    case 'Draft':
      return 'DRAFT'
    case 'In Progress':
      return 'IN_PROGRESS'
    case 'Submitted':
      return 'SUBMITTED'
    case 'Approved':
      return 'APPROVED'
    case 'Rejected':
      return 'REJECTED'
    case 'Sent Back':
      return 'SENT_BACK'
    default:
      return undefined
  }
}

type PlanListFilterParams = Omit<AssortmentPlansFilters, 'page' | 'pageSize'>

// Query params shared by GET /plans and GET /plans/ids
function buildPlanFilterParams({
  search,
  status,
  owner_id,
  hierarchy_id,
  season_id,
}: PlanListFilterParams): Record<string, string> {
  const params: Record<string, string> = {
    sort: '-modified_at', // Newest first
  }
  if (search && search.trim() !== '') {
    params.q = search.trim()
  }
  const apiStatus = mapStatusToApi(status ?? '')
  if (apiStatus) {
    params.status = apiStatus
  }
  if (owner_id) {
    params.owner_id = owner_id
  }
  if (hierarchy_id) {
    params.hierarchy_id = hierarchy_id
  }
  if (season_id) {
    params.season_id = season_id
  }
  return params
}

export async function fetchAssortmentPlans(
  filters: AssortmentPlansFilters = {}
): Promise<AssortmentPlansResponse> {
  const { page = 1, pageSize = 20, ...listFilters } = filters

  // Convert page/pageSize to offset/limit
  const offset = (page - 1) * pageSize
  const params = {
    ...buildPlanFilterParams(listFilters),
    offset,
    limit: pageSize,
  }

  const response = await api.get<PaginatedPlansResponse>('/plans', { params })

  const { data: items, pagination } = response.data

  // Map API plans to frontend format
  const plans = items.map(mapApiPlanToFrontend)

  // Calculate pagination info
  const totalCount = pagination.total
  const totalPages = Math.ceil(totalCount / pageSize)

  return {
    plans,
    totalCount,
    totalPages,
    currentPage: page,
  }
}

// Ids of every plan matching the list filters, across all pages
export async function fetchAssortmentPlanIds(
  filters: PlanListFilterParams = {}
): Promise<string[]> {
  const response = await api.get<{ ids: string[]; total: number }>(
    '/plans/ids',
    { params: buildPlanFilterParams(filters) }
  )
  return response.data.ids
}

// Delete a plan
export async function deletePlan(planId: string): Promise<void> {
  await api.delete(`/plans/${planId}`)
}

// Copy a plan
export interface CopyPlanParams {
  name: string
  sourceVersionId: string
  hierarchyId?: string | null
}

export async function copyPlan(params: CopyPlanParams): Promise<void> {
  await api.post('/plans', {
    name: params.name,
    plan_type: 'CORE', // All plans are now Core
    source_version_id: params.sourceVersionId,
    created_from: 'COPY',
    // The copy also takes the source's header and MFPs.
    copy_header: true,
    hierarchy_id: params.hierarchyId ?? undefined,
  })
}

// Update plan name
export async function updatePlanName(
  planId: string,
  name: string
): Promise<void> {
  await api.patch(`/plans/${planId}`, { name })
}

// Update plan header fields (generic partial update)
export async function updatePlanHeader(
  planId: string,
  updates: Record<string, string | number | string[] | null>
): Promise<ApiPlanDetailResponse> {
  const response = await api.patch<ApiPlanDetailResponse>(
    `/plans/${planId}`,
    updates
  )
  return response.data
}

// Freeze a plan (blocks all content/version/approval edits until unfrozen)
export async function freezePlan(
  planId: string
): Promise<ApiPlanDetailResponse> {
  const response = await api.post<ApiPlanDetailResponse>(
    `/plans/${planId}/freeze`
  )
  return response.data
}

// Unfreeze a plan (resumes editing without changing approval status)
export async function unfreezePlan(
  planId: string
): Promise<ApiPlanDetailResponse> {
  const response = await api.post<ApiPlanDetailResponse>(
    `/plans/${planId}/unfreeze`
  )
  return response.data
}
